// FI Income Calendar (CIO request 2026-09-28): next ex-dividend date and estimated distribution per
// held ETF. DISPLAY ONLY — fetched separately from get_fixed_income_snapshot so the page's heavy
// FRED-backed read is not slowed. Backend: get_fi_income_calendar / strategy/fi_income_calendar.py.
import React, { useRef, useState } from "react";
import { chipClass, CARD_CLASS, MICRO_LABEL } from "@components/cockpit";
import { API_BASE_URL } from "@lib/config";
import { useLivePoll } from "../../hooks/useLivePoll";
import {
  amountSourceLabel,
  daysLabel,
  exDateLabel,
  fmtMoney,
  fmtPerShare,
  morningstarFreshness,
  type IncomeCalendar,
} from "@lib/fiIncomeCalendar";

const ui = {
  cardBg: "var(--card-bg)",
  cardBorder: "var(--card-border)",
  textPrimary: "var(--text-primary)",
  textSecondary: "var(--text-secondary)",
  textMuted: "var(--text-muted)",
  shadow: "var(--shadow-soft)",
};
const cardStyle: React.CSSProperties = {
  backgroundColor: ui.cardBg,
  border: `1px solid ${ui.cardBorder}`,
  color: ui.textPrimary,
  boxShadow: ui.shadow,
};
const secondary: React.CSSProperties = { color: ui.textSecondary };
const muted: React.CSSProperties = { color: ui.textMuted };

const FLAG_TEXT: Record<string, string> = {
  NO_MORNINGSTAR_DATA: "Not in the Morningstar roster",
  NO_NEXT_EX_DATE: "No ex-date",
  NO_AMOUNT_ESTIMATE: "No estimate",
};

export const IncomeCalendarCard: React.FC<{ enabled: boolean }> = ({ enabled }) => {
  const [data, setData] = useState<IncomeCalendar | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef<AbortController | null>(null);

  const load = async () => {
    if (inFlight.current) inFlight.current.abort();
    const controller = new AbortController();
    inFlight.current = controller;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/get_fi_income_calendar`, { signal: controller.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData((await res.json()) as IncomeCalendar);
      setError(null);
    } catch (e: any) {
      if (e?.name === "AbortError") return;
      setError(e?.message || "Failed to load");
    } finally {
      if (inFlight.current === controller) setLoading(false);
    }
  };

  // Same cadence as the page (30 min); distribution data changes a few times a month.
  useLivePoll(load, 30 * 60_000, { enabled });

  const fresh = data ? morningstarFreshness(data.morningstar) : null;

  return (
    <div className={`${CARD_CLASS} mb-8`} style={cardStyle} data-testid="fi-income-calendar">
      <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
        <div>
          <div className={MICRO_LABEL}>Income Calendar</div>
          <div className="text-xs mt-1" style={muted}>
            Next ex-dividend date and estimated distribution per holding. Estimates only — bond-ETF payouts vary month to month.
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {fresh && <span className={chipClass(fresh.tone)} title={data?.morningstar.error ?? undefined}>{fresh.text}</span>}
          {data && !data.ibkr.available && (
            <span className={chipClass("warning")} title={data.ibkr.error ?? undefined}>
              IBKR dividend fields unavailable — dates projected
            </span>
          )}
        </div>
      </div>

      {error && !data && (
        <div className="text-sm text-down">Income calendar unavailable: {error}</div>
      )}
      {loading && !data && <div className="text-sm" style={muted}>Loading income calendar…</div>}

      {data && (
        <>
          <div className="flex flex-wrap gap-6 mb-3">
            <div>
              <div className={MICRO_LABEL}>Est. next 30 days</div>
              <div className="text-lg font-semibold">{fmtMoney(data.totals.est_next_30d)}</div>
              <div className="text-[11px]" style={muted}>{data.totals.est_next_30d_count} holdings go ex-dividend</div>
            </div>
            <div>
              <div className={MICRO_LABEL}>Trailing 12-mo at current shares</div>
              <div className="text-lg font-semibold">{fmtMoney(data.totals.est_annual_income)}</div>
              <div className="text-[11px]" style={muted}>What the last year's distributions would pay on today's shares</div>
            </div>
            {data.totals.holdings_without_estimate > 0 && (
              <div>
                <div className={MICRO_LABEL}>Not estimated</div>
                <div className="text-lg font-semibold text-warn">
                  {data.totals.holdings_without_estimate}
                </div>
                <div className="text-[11px]" style={muted}>excluded from the totals</div>
              </div>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-[11px]" style={muted}>
                  <th className="pr-4 py-1 font-normal">ETF</th>
                  <th className="pr-4 py-1 font-normal text-right">Shares</th>
                  <th className="pr-4 py-1 font-normal">Next ex-date</th>
                  <th className="pr-4 py-1 font-normal text-right">Est. $/share</th>
                  <th className="pr-4 py-1 font-normal text-right">Est. payment</th>
                  <th className="pr-4 py-1 font-normal">Last ex-date</th>
                  <th className="pr-4 py-1 font-normal text-right" title="What the fund paid per share, not cash Helion received">Last $/share</th>
                  <th className="pr-4 py-1 font-normal text-right">12-mo yield</th>
                  <th className="py-1 font-normal">Freq.</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => {
                  const ex = exDateLabel(r);
                  const soon = r.days_to_ex != null && r.days_to_ex <= 7;
                  return (
                    <tr key={r.ticker} className="border-t" style={{ borderColor: ui.cardBorder }}>
                      <td className="pr-4 py-1 font-medium">
                        {r.ticker}
                        {r.flags.filter((f) => f === "NO_MORNINGSTAR_DATA").map((f) => (
                          <span key={f} className={`${chipClass("warning")} ml-2`}>{FLAG_TEXT[f]}</span>
                        ))}
                      </td>
                      <td className="pr-4 py-1 text-right" style={secondary}>{r.shares.toLocaleString("en-US")}</td>
                      <td className="pr-4 py-1" title={ex.title}>
                        <span style={soon ? { fontWeight: 600 } : undefined}>{ex.text}</span>
                        {ex.tag && (
                          <span className={`${chipClass(ex.tag === "declared" ? "info" : "neutral")} ml-2`}>{ex.tag}</span>
                        )}
                        {r.days_to_ex != null && <span className="text-[11px] ml-2" style={muted}>{daysLabel(r.days_to_ex)}</span>}
                      </td>
                      <td className="pr-4 py-1 text-right" title={amountSourceLabel(r.est_per_share_source)}>{fmtPerShare(r.est_per_share)}</td>
                      <td className="pr-4 py-1 text-right font-medium">{fmtMoney(r.est_payment)}</td>
                      <td className="pr-4 py-1" style={secondary}>{r.last_ex_date ?? "—"}</td>
                      <td className="pr-4 py-1 text-right" style={secondary}>{fmtPerShare(r.last_per_share)}</td>
                      <td className="pr-4 py-1 text-right" style={secondary}>
                        {r.yield_12m_pct == null ? "—" : `${r.yield_12m_pct.toFixed(2)}%`}
                      </td>
                      <td className="py-1 text-[11px]" style={muted}>{r.frequency ?? "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="text-[11px] mt-3 space-y-0.5" style={muted}>
            <div>Pay date not shown yet (it arrives with the Morningstar Data Feed); it is typically a few business days after the ex-date.</div>
            <div>Shares must be held before the ex-date. Est. payment = shares held now × est. $/share. "Last $/share" is what the fund paid, not cash received.</div>
          </div>
        </>
      )}
    </div>
  );
};
